import React from "react";
import { Box, Grid, Typography, ButtonBase, Stack } from "@mui/material";

// 画面遷移の管理
import { useNavigate } from "react-router-dom";

// アイコンインポート
import MyPageIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import EventNoteIcon from "@mui/icons-material/EventNoteTwoTone";
import EqualizerSharpIcon from "@mui/icons-material/EqualizerSharp";
import PhoneForwardedOutlinedIcon from "@mui/icons-material/PhoneForwardedOutlined";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import DoNotDisturbRoundedIcon from "@mui/icons-material/DoNotDisturbRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const colors = {
  bg: "#2D2C3A",
  panel: "#1F2530",
  panel2: "#252B3A",
  panel3: "#30364A",
  border: "rgba(217, 217, 217, 0.12)",
  borderStrong: "rgba(197, 231, 99, 0.35)",
  text: "#F5F7FA",
  muted: "#AAB4C3",
  accent: "#C5E763",
  accentSoft: "rgba(197, 231, 99, 0.12)",
  danger: "#ff8579",
  blue: "#64B5F6",
};

// ボタン一覧
const shortcutButtons = [
  {
    label: "見込み顧客",
    description: "次の商談につながる情報を整理",
    icon: PhoneForwardedOutlinedIcon,
    path: "/Prospective_customers",
    color: colors.accent,
    tag: "営業入口",
  },
  {
    label: "既存顧客",
    description: "関係性・対応履歴を確認",
    icon: SwitchAccountOutlinedIcon,
    path: "/ExistingCustomers",
    color: colors.accent,
    tag: "フォロー",
  },
  {
    label: "取引停止企業",
    description: "停止理由と再開候補を確認",
    icon: DoNotDisturbRoundedIcon,
    path: "/SDMS_stoppedCustomers",
    color: colors.danger,
    tag: "確認",
  },
  {
    label: "リリースノート",
    description: "更新内容・改善内容を確認",
    icon: EventNoteIcon,
    path: "/ReleaceNote",
    color: colors.blue,
    tag: "お知らせ",
  },
  {
    label: "マイページ",
    description: "自分の情報を確認",
    icon: MyPageIcon,
    path: "/my_page",
    color: colors.blue,
    tag: "個人設定",
  },
  {
    label: "設定",
    description: "システム設定を管理",
    icon: SettingsIcon,
    path: "/setting",
    color: colors.blue,
    tag: "管理",
  },
];

/// <summary>
/// メインコンポーネント
/// </summary>
export default function DashHome() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at top right, rgba(197, 231, 99, 0.10), transparent 34%),
           radial-gradient(circle at bottom left, rgba(100, 181, 246, 0.08), transparent 30%),
           ${colors.bg}`,
        color: colors.text,
        px: { xs: 2, md: 4 },
        py: { xs: 3, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1120, mx: "auto" }}>
        {/* ヘッダー */}
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.2, md: 2.8 },
            borderRadius: 4,
            bgcolor: "rgba(31, 37, 48, 0.78)",
            border: `1px solid ${colors.border}`,
            boxShadow: "0 16px 44px rgba(0, 0, 0, 0.20)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1.4} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "16px",
                  display: "grid",
                  placeItems: "center",
                  background: colors.accentSoft,
                  border: `1px solid ${colors.borderStrong}`,
                  flexShrink: 0,
                }}
              >
                <EqualizerSharpIcon
                  sx={{ fontSize: 24, color: colors.accent }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 850,
                    color: colors.accent,
                    letterSpacing: "0.12em",
                    lineHeight: 1,
                  }}
                >
                  SALES DASHBOARD
                </Typography>

                <Typography
                  sx={{
                    mt: 0.7,
                    fontSize: { xs: 20, md: 23 },
                    fontWeight: 850,
                    lineHeight: 1.25,
                  }}
                >
                  営業管理ホーム
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                color: colors.muted,
                fontSize: 13.5,
                lineHeight: 1.7,
                maxWidth: 420,
              }}
            ></Typography>
          </Stack>
        </Box>

        {/* ショートカット */}
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            bgcolor: "rgba(31, 37, 48, 0.90)",
            border: `1px solid ${colors.border}`,
            boxShadow: "0 18px 50px rgba(0, 0, 0, 0.22)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "flex-end" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 2.2 }}
          >
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 850 }}>
                ショートカット
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 12.8, mt: 0.4 }}>
                よく使う機能をまとめています。
              </Typography>
            </Box>

            <Box
              sx={{
                px: 1.2,
                py: 0.55,
                borderRadius: 999,
                bgcolor: colors.accentSoft,
                border: `1px solid ${colors.borderStrong}`,
                color: colors.accent,
                fontSize: 11.5,
                fontWeight: 800,
              }}
            >
              6 MENU
            </Box>
          </Stack>

          <Grid container spacing={1.6}>
            {shortcutButtons.map((btn) => {
              const IconComponent = btn.icon;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={btn.label}>
                  <ButtonBase
                    onClick={() => navigate(btn.path)}
                    sx={{
                      width: "100%",
                      minHeight: 126,
                      p: 1.8,
                      borderRadius: 3,
                      bgcolor: colors.panel2,
                      border: `1px solid ${colors.border}`,
                      textAlign: "left",
                      display: "block",
                      overflow: "hidden",
                      position: "relative",
                      transition:
                        "transform .18s ease, border-color .18s ease, background-color .18s ease, box-shadow .18s ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background: `linear-gradient(135deg, ${btn.color}1F, transparent 46%)`,
                        opacity: 0,
                        transition: "opacity .18s ease",
                      },
                      "&:hover": {
                        transform: "translateY(-2px)",
                        bgcolor: colors.panel3,
                        borderColor: btn.color,
                        boxShadow: "0 12px 28px rgba(0, 0, 0, 0.24)",
                      },
                      "&:hover::before": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Stack
                      spacing={1.4}
                      sx={{ position: "relative", zIndex: 1 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box
                          sx={{
                            width: 43,
                            height: 43,
                            borderRadius: "15px",
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "rgba(255,255,255,0.045)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent
                            sx={{ fontSize: 24, color: btn.color }}
                          />
                        </Box>

                        <ArrowForwardRoundedIcon
                          sx={{
                            fontSize: 18,
                            color: colors.muted,
                            flexShrink: 0,
                          }}
                        />
                      </Stack>

                      <Box>
                        <Typography
                          sx={{
                            color: colors.text,
                            fontWeight: 850,
                            fontSize: 15.5,
                            lineHeight: 1.35,
                          }}
                        >
                          {btn.label}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.65,
                            color: colors.muted,
                            fontSize: 12.3,
                            lineHeight: 1.55,
                          }}
                        >
                          {btn.description}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "inline-flex",
                          alignSelf: "flex-start",
                          px: 0.95,
                          py: 0.28,
                          borderRadius: 999,
                          color: btn.color,
                          bgcolor: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          fontSize: 10.8,
                          fontWeight: 800,
                        }}
                      >
                        {btn.tag}
                      </Box>
                    </Stack>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
